import _ from 'lodash'
import Vue from 'vue'

import NewTemplateCandidateContainer from '@/components/interview/email/NewTemplateCandidateContainer.vue'
import NewTemplateCandidateItem from '@/components/interview/email/NewTemplateCandidateItem.vue'
import NewTemplateDashboardButton from '@/components/interview/email/NewTemplateDashboardButton.vue'

export const DEFAULT_INTERVIEW_EMAIL_THEME_COLOR = '#0096cc'

export function normalizeInterviewEmailThemeColor (color) {
  const normalizedColor = (color || '').trim().toLowerCase()
  const colorWithPrefix = normalizedColor.charAt(0) === '#' ? normalizedColor : `#${normalizedColor}`
  return /^#[0-9a-f]{6}$/.test(colorWithPrefix) ? colorWithPrefix : DEFAULT_INTERVIEW_EMAIL_THEME_COLOR
}

function removeVueCommentNodes (element) {
  _.forEach(Array.from(element.childNodes), node => {
    if (node.nodeType === 8) {
      element.removeChild(node)
      return
    }
    removeVueCommentNodes(node)
  })
}

function restoreMsoLineHeightRule (element) {
  const elements = [element, ...Array.from(element.querySelectorAll('[style*="line-height"]'))]
  _.forEach(elements, currentElement => {
    const style = currentElement.getAttribute('style') || ''
    if (/(^|;)\s*line-height\s*:/.test(style) && !style.includes('mso-line-height-rule')) {
      currentElement.setAttribute('style', `${style} mso-line-height-rule: exactly;`)
    }
  })
}

function renderVueComponentToHtml (Component, propsData) {
  const ComponentConstructor = Vue.extend(Component)
  const componentInstance = new ComponentConstructor({ propsData })
  try {
    componentInstance.$mount()
    removeVueCommentNodes(componentInstance.$el)
    restoreMsoLineHeightRule(componentInstance.$el)
    return componentInstance.$el.outerHTML
  } finally {
    componentInstance.$destroy()
  }
}

function normalizeCandidateContent (content) {
  return String(content || '').replace(/^(?:\s*<br\b[^>]*>\s*)+/gi, '')
}

export function buildNewTemplateCandidateContainer (languageCode, candidateContent = '') {
  return renderVueComponentToHtml(NewTemplateCandidateContainer, {
    candidateContent: normalizeCandidateContent(candidateContent),
    languageCode
  })
}

export function buildNewTemplateCandidateSection (candidateContent) {
  return `<table data-interview-candidate-section="true" role="presentation" width="600" cellspacing="0" cellpadding="0" border="0" bgcolor="#efefef" style="width: 600px; border-collapse: collapse; border-spacing: 0; background-color: #efefef;"><tbody><tr><td style="padding: 16px;">${candidateContent}</td></tr></tbody></table>`
}

export function buildNewTemplateCandidateItem (options) {
  const {
    candidateId,
    candidateName,
    rateLink,
    languageCode,
    themeColor,
    showSummary,
    summary,
    allowGenerateSummary,
    showFitScore,
    fitScoreImageUrl,
    fitScoreValue,
    allowGenerateFitScore,
    showLoading
  } = options
  return renderVueComponentToHtml(NewTemplateCandidateItem, {
    allowGenerateFitScore,
    allowGenerateSummary,
    candidateId,
    candidateName: String(candidateName || ''),
    fitScoreImageUrl: String(fitScoreImageUrl || ''),
    fitScoreValue: String(fitScoreValue || ''),
    languageCode,
    rateLink: String(rateLink || ''),
    showFitScore,
    showLoading,
    showSummary,
    summary: String(summary || ''),
    themeColor: normalizeInterviewEmailThemeColor(themeColor)
  })
}

export function buildNewTemplateDashboardButton (languageCode, themeColor) {
  const dashboardLink = '$' + '{view_dashboard_link}'
  return renderVueComponentToHtml(NewTemplateDashboardButton, {
    dashboardLink,
    languageCode,
    themeColor: normalizeInterviewEmailThemeColor(themeColor)
  })
}

export function replaceNewTemplateDashboardButton (content, languageCode, themeColor) {
  if (!content || !content.includes('$' + '{view_dashboard_link}')) {
    return content
  }
  const dashboardLink = '$' + '{view_dashboard_link}'
  const contentWrapper = document.createElement('div')
  contentWrapper.innerHTML = content
  let dashboardLinkElement = null
  _.forEach(contentWrapper.querySelectorAll('a'), linkElement => {
    if (!dashboardLinkElement && linkElement.getAttribute('href') === dashboardLink) {
      dashboardLinkElement = linkElement
    }
  })
  if (!dashboardLinkElement) {
    return content
  }

  let dashboardButtonElement = dashboardLinkElement.closest('[data-interview-dashboard-button="true"], .view-dashboard-btn-content')
  if (!dashboardButtonElement) {
    let tableElement = dashboardLinkElement.closest('table')
    while (tableElement) {
      if (tableElement.getAttribute('width') === '600') {
        dashboardButtonElement = tableElement
        break
      }
      tableElement = tableElement.parentElement && tableElement.parentElement.closest('table')
    }
  }
  if (!dashboardButtonElement) {
    return content
  }

  const dashboardButtonWrapper = document.createElement('div')
  dashboardButtonWrapper.innerHTML = buildNewTemplateDashboardButton(languageCode, themeColor)
  dashboardButtonElement.parentNode.replaceChild(dashboardButtonWrapper.firstElementChild, dashboardButtonElement)
  return contentWrapper.innerHTML
}

function findCandidateSection (placeholderElement) {
  let tableElement = placeholderElement.closest('table')
  while (tableElement) {
    if (tableElement.getAttribute('data-interview-candidate-section') === 'true') {
      return tableElement
    }
    const width = tableElement.getAttribute('width') || ''
    const backgroundColor = `${tableElement.getAttribute('bgcolor') || ''} ${tableElement.getAttribute('style') || ''}`.toLowerCase()
    const cellElement = placeholderElement.closest('td')
    const cellStyle = cellElement ? cellElement.getAttribute('style') || '' : ''
    if (width === '600' && backgroundColor.includes('#efefef') && /padding\s*:\s*16px/i.test(cellStyle)) {
      return tableElement
    }
    tableElement = tableElement.parentElement && tableElement.parentElement.closest('table')
  }
  return null
}

function isStructuredInterviewSection (tableElement) {
  const hasInterviewField = Boolean(tableElement.querySelector('tr[data-interview-field]'))
  if (!hasInterviewField) {
    return false
  }
  if (tableElement.getAttribute('data-interview-info-section') === 'true') {
    return true
  }

  const width = tableElement.getAttribute('width') || ''
  const backgroundColor = `${tableElement.getAttribute('bgcolor') || ''} ${tableElement.getAttribute('style') || ''}`.toLowerCase()
  const firstRow = tableElement.rows && tableElement.rows[0]
  const firstCell = firstRow && firstRow.cells[0]
  const cellStyle = firstCell ? firstCell.getAttribute('style') || '' : ''
  const innerTable = tableElement.querySelector('table[width="568"]')
  const innerBackgroundColor = innerTable
    ? `${innerTable.getAttribute('bgcolor') || ''} ${innerTable.getAttribute('style') || ''}`.toLowerCase()
    : ''
  return width === '600' &&
    backgroundColor.includes('#efefef') &&
    /padding\s*:\s*16px/i.test(cellStyle) &&
    Boolean(innerTable) &&
    innerBackgroundColor.includes('#ffffff')
}

function findDashboardInsertionTarget (contentWrapper, candidateSection) {
  let insertionTarget = candidateSection
  _.forEach(contentWrapper.querySelectorAll('table'), tableElement => {
    const position = candidateSection.compareDocumentPosition(tableElement)
    if ((position & Node.DOCUMENT_POSITION_FOLLOWING) && isStructuredInterviewSection(tableElement)) {
      insertionTarget = tableElement
    }
  })
  return insertionTarget
}

export function replaceNewTemplateCandidateSection (content, placeholder, candidateContent, languageCode, themeColor) {
  if (!content || !placeholder || !content.includes(placeholder)) {
    return content
  }

  const normalizedContent = replaceNewTemplateDashboardButton(content, languageCode, themeColor)
  const dashboardLink = '$' + '{view_dashboard_link}'
  const hasDashboard = normalizedContent.includes(dashboardLink) || /data-interview-dashboard-button=["']true["']|view-dashboard-btn-content/i.test(normalizedContent)
  const placeholderMarker = '<span data-interview-candidate-placeholder="true"></span>'
  const contentWrapper = document.createElement('div')
  contentWrapper.innerHTML = normalizedContent.replace(placeholder, placeholderMarker)
  const placeholderElement = contentWrapper.querySelector('[data-interview-candidate-placeholder="true"]')
  if (!placeholderElement) {
    return normalizedContent
  }

  let candidateSection = findCandidateSection(placeholderElement)
  const candidateWrapper = document.createElement('div')
  candidateWrapper.innerHTML = candidateSection
    ? candidateContent
    : buildNewTemplateCandidateSection(candidateContent)
  const candidateElement = candidateWrapper.firstElementChild
  if (!candidateElement || !placeholderElement.parentNode) {
    return normalizedContent
  }
  placeholderElement.parentNode.replaceChild(candidateElement, placeholderElement)
  if (!candidateSection) {
    candidateSection = candidateElement
  }

  let dashboardElement = contentWrapper.querySelector('[data-interview-dashboard-button="true"], .view-dashboard-btn-content')
  if (!dashboardElement && !hasDashboard) {
    const dashboardWrapper = document.createElement('div')
    dashboardWrapper.innerHTML = buildNewTemplateDashboardButton(languageCode, themeColor)
    dashboardElement = dashboardWrapper.firstElementChild
  }
  if (dashboardElement) {
    const insertionTarget = findDashboardInsertionTarget(contentWrapper, candidateSection)
    if (insertionTarget.parentNode) {
      insertionTarget.parentNode.insertBefore(dashboardElement, insertionTarget.nextSibling)
    }
  }
  return contentWrapper.innerHTML
}
